import { useContext, useEffect, useMemo, useState } from 'react';
import {
  TableColumnSizingOptions,
  useTableColumnSizing_unstable as useTableColumnSizing,
} from '@fluentui/react-components';
import { UseTableColumnSizingParams } from '@shared/types';
// Direct file import: the contexts barrel pulls LayoutContext -> '@shared/components', closing a cycle back into '@shared/hooks'.
import { ToastContext } from '../contexts/ToastContext.context';

/**
 * Persist user‐resized column widths in localStorage, and show a one‐time
 * toast pointing out the feature (on first load only).
 *
 * @param storageKey  unique key under which to save/load JSON widths
 * @param defaults    your default UseTableColumnSizingParams
 */
export const usePersistedColumnSizing = <T,>(storageKey: string, defaults: UseTableColumnSizingParams) => {
  const { show } = useContext(ToastContext);

  // load previously-saved widths
  const [savedWidths, setSavedWidths] = useState<Record<string, number> | null>(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || 'null');
    } catch {
      return null;
    }
  });

  // On first-ever mount (no savedWidths), show toast & seed storage with defaults
  useEffect(() => {
    if (savedWidths === null) {
      // show the one-time info (TEMPORARILY DISABLED)
      // show('info', 'Column resizing is enabled - drag any column edge and your widths will be remembered.');
      console.warn('usePersistedColumnSizing: no saved widths found, using defaults.');

      // build an initial widths map from your defaults
      const initial: Record<string, number> = {};
      if (defaults.columnSizingOptions) {
        for (const [colId, opts] of Object.entries(defaults.columnSizingOptions)) {
          const w = opts.defaultWidth ?? opts.idealWidth;
          if (w === undefined)
            console.warn(`usePersistedColumnSizing: no defaultWidth or idealWidth for column "${colId}"`);
          else initial[colId] = w;
        }
      }

      // persist & update state so we never show again
      localStorage.setItem(storageKey, JSON.stringify(initial));
      setSavedWidths(initial);
    }
  }, [savedWidths]);

  // Merge saved widths into sizing params, and hook persistence on resize
  const params: UseTableColumnSizingParams = useMemo(() => {
    let p: UseTableColumnSizingParams = { ...defaults };

    // overlay saved widths
    if (savedWidths && p.columnSizingOptions) {
      const merged: TableColumnSizingOptions = {};
      for (const [colId, opts] of Object.entries(p.columnSizingOptions)) {
        const saved = savedWidths[colId];
        merged[colId] = {
          ...opts,
          defaultWidth: saved ?? opts.defaultWidth,
          idealWidth: saved ?? opts.idealWidth,
        };
      }
      p = { ...p, columnSizingOptions: merged };
    }

    // finally inject our persistence callback
    p.onColumnResize = (_e, data) => {
      const next = { ...(savedWidths || {}), [data.columnId]: data.width };
      localStorage.setItem(storageKey, JSON.stringify(next));
      setSavedWidths(next);
    };

    return p;
  }, [savedWidths]);

  // 3) hand back the full sizing state
  return useTableColumnSizing<T>(params);
};
